import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { useLang, MANIFESTO, whatsappLink } from "../i18n";
import { Reveal, Eyebrow } from "../components/Reveal";
import HeroSection from "../components/home/HeroSection";
import ProductsGrid from "../components/home/ProductsGrid";
import Marquee from "../components/Marquee";
import EditorialProject from "../components/EditorialProject";
import BeforeAfter from "../components/BeforeAfter";
import Stats from "../components/Stats";
import { WhatsAppIcon } from "../components/WhatsAppButton";
import EditorialBreak from "../components/EditorialBreak";
import Signature from "../components/Signature";

const BREAKS = {
  pergola: { image: "arte-infissi/images/d79cd5c5-649b-4ebf-b4a1-d125142cea59.webp", it: ["Vivere fuori, come dentro.", "Una pergola sul mare: struttura in alluminio, ombra calibrata, nessun confine tra casa e terrazza."], en: ["Living outside, like inside.", "A pergola over the sea: aluminium structure, measured shade, no boundary between house and terrace."] },
  veranda: { image: "arte-infissi/images/0acf5ca9-dc38-48cc-86ce-27bcb616a14d.webp", it: ["Chiudere senza nascondere.", "Vetrate panoramiche senza montanti: il paesaggio resta tutto, il vento resta fuori."], en: ["Enclose without hiding.", "Mullion-free panoramic glass: the landscape stays whole, the wind stays out."] },
};

const FALLBACK_HERO = "arte-infissi/images/be244c09-3e51-4ada-99a7-f8af38a2b723.webp";

export default function Home() {
  const { t, lang } = useLang();
  const [featured, setFeatured] = useState([]);
  const [baProject, setBaProject] = useState(null);
  const [settings, setSettings] = useState({ hero_video: "", hero_poster: "" });

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      const f = data.filter((p) => p.featured);
      setFeatured((f.length ? f : data).slice(0, 4));
      setBaProject(data.find((p) => p.before_image && p.cover_image) || null);
    }).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const heroPoster = settings.hero_poster || FALLBACK_HERO;
  const heroVideo = settings.hero_video ? imgSrc(settings.hero_video) : "";

  return (
    <div data-testid="home-page">
      <HeroSection heroVideo={heroVideo} heroPoster={heroPoster} />

      <Marquee />

      {/* INTRO STATEMENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-3"><Eyebrow>{t.intro_eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1} className="lg:col-span-9">
            <p data-testid="intro-statement" className="font-serif text-3xl sm:text-4xl lg:text-6xl font-light leading-[1.15] tracking-tight text-[#F3EFE6]">
              {t.intro_title}
            </p>
            <Link to="/about" data-testid="intro-about-link" className="group mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C8A86B]">
              {t.nav_about} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <ProductsGrid />

      <EditorialBreak testid="home-break-pergola" eyebrow="Pergotende" image={BREAKS.pergola.image} headline={BREAKS.pergola[lang][0]} line={BREAKS.pergola[lang][1]} />

      {/* FEATURED EDITORIAL */}
      <section data-testid="home-featured" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <Reveal>
            <Eyebrow>{t.featured_eyebrow}</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{t.featured_title}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/projects" data-testid="view-all-projects-link" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#C8A86B]">
              {t.view_all} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-20 flex flex-col gap-24 sm:gap-36">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={0.05}><EditorialProject project={p} index={i} /></Reveal>
          ))}
        </div>
      </section>

      <EditorialBreak testid="home-break-veranda" eyebrow="Vetrate Panoramiche" image={BREAKS.veranda.image} headline={BREAKS.veranda[lang][0]} line={BREAKS.veranda[lang][1]} align="right" />

      {/* BEFORE / AFTER */}
      {baProject && (
        <section data-testid="home-before-after" className="border-t border-[#1C1A17] bg-[#0A0A09]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <Reveal className="lg:col-span-4">
                <Eyebrow>{t.ba_eyebrow}</Eyebrow>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{t.ba_title}</h2>
                <p className="mt-5 text-sm sm:text-base font-light text-[#A39E93] leading-relaxed">{t.ba_sub}</p>
                <p className="mt-8 font-serif text-xl text-[#F3EFE6]">{lang === "it" ? baProject.title_it : baProject.title_en}</p>
                <Link to={`/projects/${baProject.id}`} data-testid="ba-view-project" className="group mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C8A86B]">
                  {t.ba_view_project} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
              <Reveal delay={0.15} className="lg:col-span-8">
                <BeforeAfter before={imgSrc(baProject.before_image)} after={imgSrc(baProject.cover_image)} alt={baProject.title_it} />
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <Stats />

      <Signature />

      {/* MANIFESTO */}
      <section className="bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36">
          <Reveal>
            <Eyebrow>{t.manifesto_eyebrow}</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6] max-w-2xl">{t.manifesto_title}</h2>
          </Reveal>
          <div className="mt-16">
            {MANIFESTO.map((ch, i) => (
              <Reveal key={ch.number} delay={i * 0.05}>
                <div data-testid={`manifesto-chapter-${ch.number}`} className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-t border-[#1C1A17] py-10 transition-colors duration-500 hover:bg-[#0F0F0E]">
                  <span className="md:col-span-2 font-mono text-3xl sm:text-4xl text-[#C8A86B]/60 transition-colors duration-500 group-hover:text-[#C8A86B]">{ch.number}</span>
                  <h3 className="md:col-span-4 font-serif text-xl sm:text-2xl font-normal text-[#F3EFE6] leading-snug">{lang === "it" ? ch.title_it : ch.title_en}</h3>
                  <p className="md:col-span-6 text-sm sm:text-base font-light text-[#A39E93] leading-relaxed">{lang === "it" ? ch.content_it : ch.content_en}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="relative overflow-hidden border-t border-[#1C1A17]">
        <div className="absolute inset-0 bronze-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <Reveal>
            <Eyebrow className="justify-center">{t.contact_band_eyebrow}</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-3xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{t.contact_band_title}</h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-band-cta" className="group inline-flex items-center gap-3 bg-[#C8A86B] px-8 py-4 text-sm uppercase tracking-[0.15em] text-[#080808] transition-colors duration-300 hover:bg-[#D8BD85]">
                <WhatsAppIcon size={16} /> {t.contact_band_cta}
              </a>
              <Link to="/contact" data-testid="contact-band-cta2" className="inline-flex items-center gap-3 border border-[#C8A86B] px-8 py-4 text-sm uppercase tracking-[0.15em] text-[#C8A86B] transition-all duration-300 hover:bg-[#C8A86B] hover:text-[#080808]">
                {t.contact_band_cta2} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
